import { useControlLock, usePermissions, usePreflightSnapshot } from '@ctcells/api'
import type { Batch } from '@ctcells/contract'
import { dataSourceFor } from '@ctcells/core'
import { useControlDisabled } from '@ctcells/realtime'

import { batchDisplayName } from '../screens/batches/batchDisplayName.ts'

import { useActiveBatches } from './activeBatchSource.ts'
import { startBlockers } from './startBlockers.ts'

/**
 * 한 배치의 시작 차단 사유. 작업 줄과 작업 상세가 **같은 것**을 쓴다.
 *
 * Pre-flight 를 부르는 조건: live · 상태 `created|queued` · 서버가 `start` 를 안 실음 ·
 * 쓰기 역할(서버가 그 경로를 `WRITE_ROLES` 로 막는다) · 연결 살아 있음. 하나라도 아니면 안 부른다.
 */
export function useStartBlockers(batch: Batch | undefined): string[] {
  const permissions = usePermissions()
  const lock = useControlLock()
  const streamStale = useControlDisabled()
  const batches = useActiveBatches()

  const canControl = permissions?.includes('control') ?? false
  const waiting =
    batch !== undefined &&
    (batch.status === 'created' || batch.status === 'queued') &&
    !batch.allowed_commands.includes('start')

  const preflight = usePreflightSnapshot({
    batchId: batch?.batch_id,
    skip: !waiting || !canControl || streamStale || dataSourceFor('batches') !== 'live',
  })

  if (batch === undefined) return []
  return startBlockers({
    status: batch.status,
    allowedCommands: batch.allowed_commands,
    streamStale,
    canControl,
    lockHeld: lock.held,
    others: batches
      .filter((other) => other.batch_id !== batch.batch_id)
      .map((other) => ({ name: batchDisplayName(other), status: other.status })),
    preflight: preflight.data,
  })
}
